import axios from 'axios';
import { createAsyncThunk, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { IQueryParams, createEntitySlice, EntityState, serializeAxiosError, ISummaryQueryParam } from 'app/shared/reducers/reducer.utils';
import { IBookingConfirmation, defaultValue } from 'app/shared/model/booking-confirmation.model';
import { getQuotationPurchaseOrderApprovalCount } from 'app/entities/quotation/quotation.reducer';

const initialState: EntityState<IBookingConfirmation> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
  bookingConfirmationTotalItems: 0,
};

const apiUrl = 'api/bookingConfirmation';

// Actions

export const getEntities = createAsyncThunk('bookingConfirmation/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<IBookingConfirmation[]>(requestUrl);
});

export const getEntitiesWithFalseAmountTransferred = createAsyncThunk(
  'bookingConfirmation/fetch_entity_list_with_false_amount_transferred',
  async ({ page, size, sort }: IQueryParams) => {
    const requestUrl = `${apiUrl}/amountTransferred${
      sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'
    }cacheBuster=${new Date().getTime()}`;
    return axios.get<IBookingConfirmation[]>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getEntity = createAsyncThunk(
  'bookingConfirmation/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<IBookingConfirmation>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getEntityWithQuotationData = createAsyncThunk(
  'bookingConfirmation/fetch_entity_with_quotation_data',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/amountTransferred/${id}`;
    return axios.get<IBookingConfirmation>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getBookingConfirmationCount = createAsyncThunk(
  'quotation/fetch_booking_confirmation_count_entity',
  async () => {
    const requestUrl = `${apiUrl}/getBookingConfirmationCount`;
    return axios.get<number>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const createEntity = createAsyncThunk(
  'bookingConfirmation/create_entity',
  async (entity: IBookingConfirmation, thunkAPI) => {
    const result = await axios.post<IBookingConfirmation>(apiUrl, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'bookingConfirmation/update_entity',
  async (entity: IBookingConfirmation, thunkAPI) => {
    const result = await axios.put<IBookingConfirmation>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'bookingConfirmation/partial_update_entity',
  async (entity: IBookingConfirmation, thunkAPI) => {
    const result = await axios.patch<IBookingConfirmation>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'bookingConfirmation/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<IBookingConfirmation>(requestUrl);
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const BookingConfirmationSlice = createEntitySlice({
  name: 'bookingConfirmation',
  initialState,
  extraReducers(builder) {
    builder

      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(getEntityWithQuotationData.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities, getEntitiesWithFalseAmountTransferred), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(getBookingConfirmationCount), (state, action) => {
        state.bookingConfirmationTotalItems = action.payload.data;
      })
      .addMatcher(
        isPending(getEntities, getEntity, getEntitiesWithFalseAmountTransferred, getEntityWithQuotationData, getBookingConfirmationCount),
        state => {
          state.errorMessage = null;
          state.updateSuccess = false;
          state.loading = true;
        }
      )
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = BookingConfirmationSlice.actions;

// Reducer
export default BookingConfirmationSlice.reducer;
